import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { SessionContextService } from "../../context/session-context.service";
import { AppUserAuth } from "../../models/user.model";
import { SessionService } from "../../services/session.service";

import { Message } from "../../../../../shared-components/src/lib/models/message.model";

import { TranslateService } from "@ngx-translate/core";
import { SpinnerService } from "shared-components";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
})
export class LoginComponent implements OnInit {
  public loginForm: UntypedFormGroup;
  public forgotPasswordForm: UntypedFormGroup;
  public verificationCodeForm: UntypedFormGroup;
  public rolesForm: UntypedFormGroup;
  public showError = false;
  public mensajes = new Array<Message>();
  public verification = false;
  public showPassword: boolean;
  public forgotPasswd = false;
  public showRoles = false;
  public roles = [];

  @Output() closeModal = new EventEmitter<boolean>();

  constructor(
    private _fb: UntypedFormBuilder,
    private readonly _translate: TranslateService,
    private readonly _sessionService: SessionService,
    private _sessionContext: SessionContextService,
    private readonly _spinnerService: SpinnerService,
    public router: Router
  ) {}

  ngOnInit() {
    this.loginForm = this._fb.group({
      userName: ["", Validators.required],
      password: ["", Validators.required],
      keepSession: [false],
      roleName: [],
      cultureInfo: [this.defaultLanguage],
    });

    this.verificationCodeForm = this._fb.group({
      verificationCode: ["", Validators.required],
    });

    this.forgotPasswordForm = this._fb.group({
      email: ["", Validators.required],
    });

    this.rolesForm = this._fb.group({
      rol: ["", Validators.required],
    });
  }

  public get defaultLanguage() {
    return this._translate.getDefaultLang();
  }

  /**
   * El campo de la db devuelve una string con el siguiente formato: "10h 50m".
   * Siempre es una agrupación de dos unidades de tiempo (año-mes, mes-día, día-hora, hora-minutos, minutos-segundos).
   * Necesitamos solo la unidad mayor.
   */
  private prepareNotificationEnviadoHace(period: string) {
    let periodoTiempo;
    if (period.includes('a')) {
      periodoTiempo =  'Hace ' + period.split('a')[0] + (period.split('a')[0] === '1' ? ' año' : ' años');
    }
    if (period.includes('y')) {
      periodoTiempo = period.split('y')[0] + (period.split('y')[0] === '1' ? ' year' : ' years') +  ' ago';
    }
    if (period.includes('m') && period.includes('d')) {
      if (this.defaultLanguage === 'es-ES') {
        periodoTiempo = 'Hace ' + period.split('m')[0] + (period.split('m')[0] === '1' ? ' mes' : ' meses');
      } else {
        periodoTiempo = period.split('m')[0] + (period.split('m')[0] === '1' ? ' month' : ' months') +  ' ago';
      }
    }
    if (period.includes('d') && period.includes('h')) {
      if (this.defaultLanguage === 'es-ES') {
        periodoTiempo = 'Hace ' + period.split('d')[0] + (period.split('d')[0] === '1' ? ' día' : ' días');
      } else {
        periodoTiempo = period.split('d')[0] + (period.split('d')[0] === '1' ? ' day' : ' days') +  ' ago';
      }
    }
    if (period.includes('h') && period.includes('m')) {
      if (this.defaultLanguage === 'es-ES') {
        periodoTiempo = 'Hace ' + period.split('h')[0] + (period.split('h')[0] === '1' ? ' hora' : ' horas');
      } else {
        periodoTiempo = period.split('h')[0] + (period.split('h')[0] === '1' ? ' hour' : ' hours') +  ' ago';
      }
    }
    if (period.includes('m') && period.includes('s')) {
      if (this.defaultLanguage === 'es-ES') {
        periodoTiempo = 'Hace ' + period.split('m')[0] + (period.split('m')[0] === '1' ? ' minuto' : ' minutos');
      } else {
        periodoTiempo = period.split('m')[0] + (period.split('m')[0] === '1' ? ' minute' : ' minutes') +  ' ago';
      }
    }
    return periodoTiempo;
  }

  private removeCustomStyles() {
    document.documentElement.style.removeProperty("--main-color");
    document.documentElement.style.removeProperty("--secondary-color");
    document.documentElement.style.removeProperty("--main-light-color");
    document.documentElement.style.removeProperty("--table-header-color");
    document.documentElement.style.removeProperty("--subtable-header-color");
    if (localStorage.getItem("customStyles")) {
      localStorage.removeItem("customStyles");
    }
  }

  public checkLogin() {
    this.showError = this.loginForm.invalid;
    if (this.loginForm.valid) {
      this.mensajes = [];
      this._spinnerService.showSpinner();
      this._sessionService
        .login(this.loginForm.value)
        .toPromise()
        .finally(() => {
          this._spinnerService.hideSpinner();
        })
        .then(
          (resp) => {
            if (resp.token) {
              this.login(resp);
            } else {
              this.verification = true;
              this.showRoles = false;
            }

            // Obtener los estilos personalizados de la entidad de control
            if (resp.lsClaimTypes && resp.lsClaimTypes.includes('D_EstiloFront')) {
              const filters = {};
              this._sessionService.getCustomStyles(filters).toPromise().finally(() => {
              }).then(
                (respStyles) => {
                  // Si se devuelven estilos custom
                  if (respStyles?.estilo) {
                    const customStyles = JSON.parse(respStyles.estilo);
                    customStyles.entidadControlId = respStyles.entidadControlId;
                    customStyles.id = respStyles.id;
                    if (customStyles.primaryColor) {
                      document.documentElement.style.setProperty(
                        "--main-color",
                        customStyles.primaryColor
                      );
                    } else {
                      document.documentElement.style.removeProperty("--main-color");
                    }
                    if (customStyles.secondaryColor) {
                      document.documentElement.style.setProperty(
                        "--secondary-color",
                        customStyles.secondaryColor
                      );
                    } else {
                      document.documentElement.style.removeProperty("--secondary-color");
                    }
                    if (customStyles.tertiaryColor) {
                      document.documentElement.style.setProperty(
                        "--main-light-color",
                        customStyles.tertiaryColor
                      );
                    } else {
                      document.documentElement.style.removeProperty("--main-light-color");
                    }
                    if (customStyles.tableHeaderColor) {
                      document.documentElement.style.setProperty(
                        "--table-header-color",
                        customStyles.tableHeaderColor
                      );
                    } else {
                      document.documentElement.style.removeProperty("--table-header-color");
                    }
                    if (customStyles.subtableHeaderColor) {
                      document.documentElement.style.setProperty(
                        "--subtable-header-color",
                        customStyles.subtableHeaderColor
                      );
                    } else {
                      document.documentElement.style.removeProperty("--subtable-header-color");
                    }
                    
                    // Actualizamos localStorage
                    if (localStorage.getItem("customStyles")) {
                      localStorage.removeItem("customStyles");
                      localStorage.setItem("customStyles", JSON.stringify(customStyles));
                    } else {
                      localStorage.setItem("customStyles", JSON.stringify(customStyles));
                    }
                    // Si no se devuelven estilos
                  } else {
                    this.removeCustomStyles();
                  }
                }, (error) => {
                  // Si no hay ningún registro para el estilo custom, en el login forzamos el borrado
                  if (error.status === 420 && error.error.ApiErrorCode === 6228) {
                    this.removeCustomStyles();
                  } else {
                    this.showError = true;
                    this.mensajes.push({
                      class: "error",
                      message: "session.customStyles.errors.noAccesible",
                    });
                  }
                }
              )
            } else {
              this.removeCustomStyles();
            }

            // Obtener notificaciones pendientes si corresponde
            if (this.loginForm.get('roleName') && this.loginForm.get('roleName').value && this.loginForm.get('roleName').value !== 'Administrador' && resp.lsClaimTypes && resp.lsClaimTypes.includes('D_CNO')) {
              this._sessionService.getNotificacionesPendientes(4).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
              }).then(
                (resp) => {
                  if (resp) {
                    const newPendingNotifications = resp.map(object => {
                      return {
                        ...object,
                        enviadoHaceFormateado: this.prepareNotificationEnviadoHace(object.enviadoHace_PeriodoTiempo)
                      }
                    });
                    this._sessionContext.pendingNotifications.next(newPendingNotifications);
                  }
                }
              )
            }
          },
          (error) => {
            if (error.status === 409) {
              this.showRoles = true;
              error.error.forEach((rol, index) => {
                this.roles.push({
                  id: rol.roleName,
                  des: rol.roleNameTraduccion,
                });
              });
            } else if (error.status === 420) {
              switch (error.error.ApiErrorCode) {
                case 1001:
                  this.showError = true;
                  this.mensajes.push({
                    class: "error",
                    message: "global.error.usuarioNoActivo",
                  });
                  break;
                default:
                  this.showError = true;
                  this.mensajes.push({
                    class: "error",
                    message: "global.error.noResponse",
                  });
                  break;
              }
            } else if (error.status === 401) {
              this.showError = true;
              this.mensajes.push({
                class: "error",
                message: "global.error.contrasenyaIncorrecta",
              });
            } else if (error.status === 403) {
              this.showError = true;
              this.mensajes.push({
                class: "error",
                message:
                  this._translate.instant("global.error.usuarioBloqueado") +
                  error.error.toString() +
                  this._translate.instant("global.error.usuarioBloqueadoPost"),
              });
            } else {
              this.showError = true;
              this.mensajes.push({
                class: "error",
                message: "global.error.noResponse",
              });
            }
          }
        );
    } else {
      this.showError = true;
      this.mensajes.push({ class: "error", message: "global.error.required" });
    }
  }

  public sendVerification() {
    if (this.verificationCodeForm.valid) {
      let verification = {
        userName: this.loginForm.get("userName").value,
        roleName: this.rolesForm.get("rol").value.des,
        email2FToken: this.verificationCodeForm.get("verificationCode").value,
        cultureInfo: this.defaultLanguage,
      };

      this.mensajes = [];
      this._spinnerService.showSpinner();
      this._sessionService
        .verificationCode(verification)
        .toPromise()
        .finally(() => {
          this._spinnerService.hideSpinner();
        })
        .then(
          (resp) => {
            this.login(resp);
            this.rolesForm.reset();
          },
          (error) => {
            if (error.status === 401) {
              this.showError = true;
              this.mensajes.push({
                class: "error",
                message: "session.error.verificationCodeError",
              });
            } else if (error.status === 420) {
              switch (error.error.ApiErrorCode) {
                case 1001:
                  this.showError = true;
                  this.mensajes.push({
                    class: "error",
                    message: "global.error.usuarioNoActivo",
                  });
                  break;
                default:
                  this.showError = true;
                  this.mensajes.push({
                    class: "error",
                    message: "global.error.noResponse",
                  });
                  break;
              }
            } else if (error.status === 403) {
              this.showError = true;
              this.mensajes.push({
                class: "error",
                message:
                  this._translate.instant("global.error.usuarioBloqueado") +
                  error.error.toString() +
                  this._translate.instant("global.error.usuarioBloqueadoPost"),
              });
            } else {
              this.showError = true;
              this.mensajes.push({
                class: "error",
                message: "session.error.verificationCodeError",
              });
            }
          }
        );
    } else {
      this.showError = true;
      this.mensajes.push({ class: "error", message: "global.error.required" });
    }
  }

  public sendEmail() {
    if (this.forgotPasswordForm.valid) {
      this.mensajes = [];
      this._spinnerService.showSpinner();
      this._sessionService
        .passwordRecovery(this.forgotPasswordForm.get("email").value)
        .toPromise()
        .finally(() => {
          this._spinnerService.hideSpinner();
        })
        .then(
          (resp) => {
            this.forgotPasswd = false;
            this.forgotPasswordForm.reset();
            this.showError = true;
            this.mensajes.push({
              class: "success",
              message: "global.emailPassword",
            });
          },
          (error) => {
            this.showError = true;
            this.mensajes.push({
              class: "error",
              message: "global.error.noResponse",
            });
          }
        );
    } else {
      this.showError = true;
      this.mensajes.push({ class: "error", message: "global.error.required" });
    }
  }

  public loginWithRol() {
    if (this.rolesForm.valid) {
      this.loginForm
        .get("roleName")
        .patchValue(this.rolesForm.get("rol").value.id);
      this.checkLogin();
      console.clear();
    } else {
      this.showError = true;
      this.mensajes.push({ class: "error", message: "session.error.noRole" });
    }
  }

  private async login(resp: AppUserAuth) {
    this._spinnerService.showSpinner();
    this._sessionContext.sessionUser = resp;
    this._sessionContext.sessionToken = resp.token;
    this._sessionContext.userClaims = resp.lsClaimTypes;
    const payload = JSON.parse(
      window.atob(this._sessionContext.sessionToken.split(".")[1])
    );
    this._sessionContext.sessionUser.role =
      payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];

    if (this.loginForm.get("keepSession").value) {
      localStorage.user = JSON.stringify(resp);
      localStorage.token = resp.token;
    }

    this._sessionContext.loadedData =
      await this._sessionService.loadDataProvider();
    this._spinnerService.hideSpinner();
    localStorage.url
      ? this.router.navigate([localStorage.url])
      : this.router.navigate([""]);
    this.closeModal.emit(false);
  }
}
